import React from "react";
import { Link } from "react-router-dom";

const Section = ({ title, children }) => (
    <section className="mt-10 border-t-2 border-ink-200 pt-8">
        <h2 className="font-display text-[28px] font-bold tracking-tight text-ink-800">{title}</h2>
        <div className="mt-5 body-prose space-y-4">{children}</div>
    </section>
);

export default function Terms() {
    return (
        <main className="min-h-screen bg-limestone-100 pt-32 pb-24" data-testid="terms-page">
            <div className="asir-stripe h-[6px]" aria-hidden="true" />
            <div className="mx-auto max-w-3xl px-5 pt-10 lg:px-8">
                <p className="eyebrow">Legal · Terms</p>
                <h1 className="section-headline mt-4" style={{ fontSize: "clamp(36px, 4.4vw, 56px)" }}>Terms of Service</h1>
                <p className="mt-4 font-mono text-[13px] font-semibold text-ink-600">
                    Last updated: February 2026 · Governed by the laws of the Kingdom of Saudi Arabia.
                </p>

                <Section title="1. Acceptance">
                    <p>By creating a NourNet account, registering a domain, or using any NourNet service you accept these Terms of Service and our Privacy Policy. If you do not agree, do not use the service.</p>
                </Section>

                <Section title="2. Eligibility">
                    <p>NourNet is available to individuals aged 18+ and to registered businesses in the Kingdom of Saudi Arabia and the GCC. Certain TLDs (<code className="rounded-[4px] bg-mudbrick-200 px-1.5 py-0.5 font-mono text-[14px]">.gov.sa</code>, <code className="rounded-[4px] bg-mudbrick-200 px-1.5 py-0.5 font-mono text-[14px]">.edu.sa</code>) are further restricted by SaudiNIC eligibility rules; we will verify eligibility before registration.</p>
                </Section>

                <Section title="3. Domain registration">
                    <p>Domain registrations are subject to the SaudiNIC registration policy and the ICANN Registrar Accreditation Agreement where applicable. Registrations are typically completed within 60 seconds; SaudiNIC-restricted TLDs may take up to 48 hours for manual eligibility review.</p>
                    <p>Renewals are automatic unless you disable auto-renew in your dashboard. We send renewal notices 30, 14, and 3 days before expiry.</p>
                </Section>

                <Section title="4. Pricing &amp; payment">
                    <p>All prices are shown in Saudi Riyals (SAR) and include 15% VAT where applicable. We accept Visa, Mastercard, Mada, Apple Pay, and Sadad (some options launching in 2026). Invoices are ZATCA-compliant and available as PDF in your dashboard.</p>
                </Section>

                <Section title="5. Acceptable use">
                    <p>You agree not to use NourNet services for:</p>
                    <ul className="list-disc ps-6 space-y-2">
                        <li>Phishing, malware distribution, or any illegal activity under Saudi law.</li>
                        <li>Mass unsolicited email (spam) — outbound mail is rate-limited and monitored.</li>
                        <li>Content that violates the Kingdom’s anti-cyber-crime law or public decency regulations.</li>
                    </ul>
                    <p>We reserve the right to suspend services for acceptable-use violations after reasonable notice (except in cases of immediate harm, where we may act without notice and inform you after the fact).</p>
                </Section>

                <Section title="6. Service availability">
                    <p>We target 99.99% monthly uptime across DNS and email services. If we miss that target in any calendar month, you will receive a pro-rated service credit automatically applied to your next invoice.</p>
                </Section>

                <Section title="7. Termination">
                    <p>You may cancel any service at any time from your dashboard; refunds follow the SaudiNIC refund window (14 days for most TLDs, none for already-registered domains as per registry policy). On termination we retain only the minimum data required by regulation.</p>
                </Section>

                <Section title="8. Liability">
                    <p>NourNet’s aggregate liability is limited to the amount you paid us in the 12 months preceding the event giving rise to the claim. We are not liable for indirect or consequential losses. Nothing in these Terms limits our liability where limitation is prohibited by law.</p>
                </Section>

                <Section title="9. Governing law">
                    <p>These Terms are governed by the laws of the Kingdom of Saudi Arabia. Any dispute will be resolved through the Saudi Centre for Commercial Arbitration (SCCA) or the competent courts of Riyadh.</p>
                </Section>

                <Section title="10. Contact">
                    <p>NourNet · Riyadh, KSA<br/>
                    Email: <a className="text-madder-500 font-semibold" href="mailto:legal@nournet.sa">legal@nournet.sa</a></p>
                </Section>

                <div className="mt-12 border-t-2 border-ink-200 pt-8">
                    <Link to="/preview" className="inline-flex h-[48px] items-center gap-2 rounded-btn border-2 border-ink-800 bg-white px-5 text-[14px] font-bold text-ink-800 hover:bg-ink-800 hover:text-white">
                        ← Back to NourNet
                    </Link>
                </div>
            </div>
        </main>
    );
}
